// The country a product page is open on, kept in the URL as ?country=XXX. Without a valid one the
// page shows the country chooser. The check uses the registry only (never availability), so deep
// links work offline.
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { normalizeCountry } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { countrySupport } from '../config/products';
import type { DataProductId } from '../config/products';
import { demoServes } from '../services/demoApi';

/** Can a person start `p` in `c`? True when the registry offers it, or the demo API serves it. */
export function canStart(c: CountryCode, p: DataProductId): boolean {
  return countrySupport(c, p).ok || demoServes(c, p);
}

export function useChosenCountry(p: DataProductId): {
  country: CountryCode | null;
  choose(c: CountryCode): void;
  change(): void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('country');
  const normalised = normalizeCountry(raw);
  const country = normalised !== null && canStart(normalised, p) ? normalised : null;

  useEffect(() => {
    if (raw === null) return;
    const next = new URLSearchParams(searchParams);
    if (country === null) next.delete('country');
    else if (raw === country) return;
    else next.set('country', country);
    setSearchParams(next, { replace: true });
  }, [raw, country, searchParams, setSearchParams]);

  const choose = useCallback(
    (c: CountryCode) => setSearchParams(new URLSearchParams({ country: c })),
    [setSearchParams],
  );
  const change = useCallback(() => setSearchParams(new URLSearchParams()), [setSearchParams]);

  return { country, choose, change };
}
